import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, Clock, Tag } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import TagPill from '../components/TagPill';
import GradientButton from '../components/GradientButton';
import { useEpisodes, getEpisodesByTag, getAllTags } from '../hooks/useEpisodes';

// Episode Row Component - matches EpisodesPage style
const EpisodeRow = ({ episode, index, currentTag }) => {
  const { id, title, description, date, tags, episodeNumber, duration } = episode;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Link
        to={`/episode/${id}`}
        className="group block"
      >
        <div className="episode-row hover:bg-[var(--bg-tertiary)]/50 p-4 md:p-6 -mx-4 md:-mx-6 rounded-xl transition-all duration-200">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            {/* Episode Number */}
            <div className="hidden md:flex flex-shrink-0 w-16 text-center">
              <span className="text-[var(--text-muted)] text-sm font-mono">
                EP {episodeNumber || '--'}
              </span>
            </div>

            {/* Main Content */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="flex-1 min-w-0">
                  {/* Title */}
                  <h3 className="font-display text-lg md:text-xl text-[var(--text-primary)] group-hover:text-accent transition-colors line-clamp-2">
                    {title}
                  </h3>

                  {/* Description - Hidden on mobile */}
                  <p className="hidden md:block text-[var(--text-secondary)] text-sm mt-1 line-clamp-1">
                    {description}
                  </p>

                  {/* Tags */}
                  {tags && tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {tags.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className={`text-xs ${
                            tag === `#${currentTag}`
                              ? 'text-accent font-semibold'
                              : 'text-[var(--text-muted)]'
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                      {tags.length > 3 && (
                        <span className="text-xs text-[var(--text-muted)]">
                          +{tags.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Meta & Play */}
                <div className="flex items-center gap-4 flex-shrink-0">
                  {/* Date/Duration */}
                  <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
                    <Clock size={14} />
                    <span>{duration || date}</span>
                  </div>

                  {/* Play Button */}
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-accent/10 group-hover:bg-accent text-accent group-hover:text-dark-primary transition-all duration-200">
                    <Play size={16} fill="currentColor" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>

      {/* Divider */}
      <div className="h-px bg-[var(--bg-tertiary)]/50 mx-4 md:mx-0" />
    </motion.div>
  );
};

// Related Tags Component
const RelatedTags = ({ allTags, currentTag }) => {
  // Filter out the current tag and show a few related ones
  const otherTags = allTags.filter(tag => tag.toLowerCase() !== currentTag.toLowerCase()).slice(0, 6);

  if (otherTags.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      className="mt-8"
    >
      <p className="text-[var(--text-muted)] text-sm uppercase tracking-wider mb-3">
        Explore Other Topics
      </p>
      <div className="flex flex-wrap gap-2">
        {otherTags.map(tag => (
          <TagPill key={tag} tag={`#${tag}`} size="small" />
        ))}
      </div>
    </motion.div>
  );
};

const EpisodesByTag = () => {
  const { tag } = useParams();
  const { episodes, loading, error } = useEpisodes();

  const filteredEpisodes = getEpisodesByTag(episodes, tag);
  const allTags = getAllTags(episodes);
  const displayTag = `#${tag}`;
  const capitalizedTag = tag.charAt(0).toUpperCase() + tag.slice(1);

  return (
    <>
      <Helmet>
        <title>{capitalizedTag} Episodes | News From The Pew</title>
        <meta name="description" content={`Browse all News From The Pew podcast episodes tagged with #${tag}. Fresh perspectives on faith, culture, and current events.`} />
        <link rel="canonical" href={`https://newsfromthepew.com/episodes/tag/${tag}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://newsfromthepew.com/episodes/tag/${tag}`} />
        <meta property="og:title" content={`${capitalizedTag} Episodes | News From The Pew`} />
        <meta property="og:description" content={`Browse all episodes tagged with #${tag}.`} />
        <meta property="og:image" content="https://newsfromthepew.com/assets/images/default_branding_image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${capitalizedTag} Episodes | News From The Pew`} />
        <meta name="twitter:description" content={`Browse all episodes tagged with #${tag}.`} />
        <meta name="twitter:image" content="https://newsfromthepew.com/assets/images/default_branding_image.png" />
      </Helmet>
      <div className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
        <Navigation />

      <main className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 overflow-hidden">
        {/* Ambient glows */}
        <div className="ambient-orb-primary -top-20 -left-20 w-72 h-72" />
        <div className="ambient-orb-secondary -bottom-20 -right-20 w-64 h-64" />

        {/* Back Navigation */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link
            to="/episodes"
            className="inline-flex items-center gap-2 text-[var(--text-secondary)] hover:text-accent
                       transition-colors mb-8 group"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            <span>All Episodes</span>
          </Link>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-accent/10 glow-ring">
              <Tag size={20} className="text-accent" />
            </div>
            <h1 className="font-display text-display-sm md:text-display-md text-[var(--text-primary)]">
              {displayTag}
            </h1>
          </div>
          <p className="text-[var(--text-secondary)] text-lg">
            {loading ? (
              'Loading episodes...'
            ) : (
              `${filteredEpisodes.length} episode${filteredEpisodes.length !== 1 ? 's' : ''} tagged with ${displayTag}`
            )}
          </p>
          <div className="h-1 w-24 bg-gradient-primary rounded-full mt-4" />
        </motion.div>

        {/* Episode List */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-[var(--bg-secondary)] rounded-2xl p-4 md:p-6"
        >
          {loading ? (
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="flex items-center gap-4 p-4">
                    <div className="hidden md:block w-16 h-4 bg-[var(--bg-tertiary)] rounded" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 bg-[var(--bg-tertiary)] rounded w-3/4" />
                      <div className="h-4 bg-[var(--bg-tertiary)] rounded w-1/2" />
                    </div>
                    <div className="w-10 h-10 bg-[var(--bg-tertiary)] rounded-full" />
                  </div>
                  <div className="h-px bg-[var(--bg-tertiary)]" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-accent mb-4">Error loading episodes</p>
              <p className="text-[var(--text-muted)] text-sm">Please try again later.</p>
            </div>
          ) : filteredEpisodes.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-[var(--text-secondary)] mb-4">No episodes found with tag {displayTag}</p>
              <GradientButton to="/episodes" icon={<ArrowLeft size={18} />} iconPosition="left">
                Browse All Episodes
              </GradientButton>
            </div>
          ) : (
            <div>
              {filteredEpisodes.map((episode, index) => (
                <EpisodeRow
                  key={episode.id}
                  episode={episode}
                  index={index}
                  currentTag={tag}
                />
              ))}
            </div>
          )}
        </motion.div>

        {/* Related Tags */}
        {!loading && !error && allTags.length > 1 && (
          <RelatedTags allTags={allTags} currentTag={tag} />
        )}
      </main>

      <Footer />
    </div>
  </>
  );
};

export default EpisodesByTag;
